/**
 * Maps site-level environmental typed preferences into Coverage Matrix
 * authored-field prefill. Typed prefs remain SSOT for report generation;
 * this bridge only seeds aligned attestation fields.
 */
import type { EnvironmentalPreferences } from '../../types/siteRag';
import type { RequirementAutoFill, RequirementAutoFillRegistry } from './compileCoverageAutoFill';

export const SPECIES_SURVEY_ENV_FIELD_KEYS = ['speciesObserved'] as const;
export const CULTURAL_ENV_FIELD_KEYS = ['tribalConsultation'] as const;

function prefFill(
  requirementId: string,
  partial: Pick<
    RequirementAutoFill,
    'fields' | 'notes' | 'fieldsFilledCount' | 'fieldsTotalCount'
  >,
): RequirementAutoFill {
  return {
    requirementId,
    ...partial,
    confidence: {
      tier: 'low',
      value: 0.45,
      rationale: 'Site environmental report config',
    },
    sourceProvider: 'typed-preferences',
    sourceUrl: '',
  };
}

/** Map environmental prefs to zero or more requirement auto-fill entries. */
export function mapEnvironmentalPrefsToCoverage(
  prefs: EnvironmentalPreferences,
): RequirementAutoFill[] {
  const out: RequirementAutoFill[] = [];

  if (prefs.speciesConcerns && prefs.speciesConcerns.length > 0) {
    out.push(
      prefFill('species-survey', {
        fields: { speciesObserved: prefs.speciesConcerns.join(', ') },
        notes: 'Pre-filled from site environmental report config (species concerns).',
        fieldsFilledCount: 1,
        fieldsTotalCount: SPECIES_SURVEY_ENV_FIELD_KEYS.length,
      }),
    );
  }

  if (prefs.tribalLandProximityKnown !== undefined) {
    out.push(
      prefFill('cultural', {
        fields: { tribalConsultation: prefs.tribalLandProximityKnown ? 'yes' : 'no' },
        notes: 'Pre-filled from site environmental report config (tribal land proximity).',
        fieldsFilledCount: 1,
        fieldsTotalCount: CULTURAL_ENV_FIELD_KEYS.length,
      }),
    );
  }

  if (prefs.dischargeWaterTarget && prefs.dischargeWaterTarget !== 'unknown') {
    out.push(
      prefFill('discharge-permit', {
        fields: {},
        notes: `Site discharge target: ${prefs.dischargeWaterTarget}. Review NPDES pathway before citing.`,
        fieldsFilledCount: 0,
        fieldsTotalCount: 0,
      }),
    );
  }

  const nepaNotes: string[] = [];
  if (prefs.expectedNepaPathway) {
    nepaNotes.push(`Expected NEPA pathway: ${prefs.expectedNepaPathway}`);
  }
  if (prefs.federalNexus !== undefined) {
    nepaNotes.push(`Federal nexus: ${prefs.federalNexus ? 'yes' : 'no'}`);
  }
  if (prefs.airBasinOverride && prefs.airBasinOverride !== 'auto') {
    nepaNotes.push(`Air basin override: ${prefs.airBasinOverride}`);
  }
  if (nepaNotes.length > 0) {
    out.push(
      prefFill('phase1-esa', {
        fields: {},
        notes: `${nepaNotes.join('. ')}.`,
        fieldsFilledCount: 0,
        fieldsTotalCount: 0,
      }),
    );
  }

  return out;
}

export function environmentalPrefsToRequirementAutoFill(
  prefs: EnvironmentalPreferences | undefined | null,
): RequirementAutoFillRegistry {
  if (!prefs) return {};
  const registry: RequirementAutoFillRegistry = {};
  for (const fill of mapEnvironmentalPrefsToCoverage(prefs)) {
    const existing = registry[fill.requirementId];
    if (existing) {
      registry[fill.requirementId] = {
        ...existing,
        fields: { ...fill.fields, ...existing.fields },
        notes: [fill.notes, existing.notes].filter(Boolean).join(' '),
        fieldsFilledCount: Object.keys({ ...fill.fields, ...existing.fields }).length,
        fieldsTotalCount: Math.max(fill.fieldsTotalCount, existing.fieldsTotalCount),
      };
    } else {
      registry[fill.requirementId] = fill;
    }
  }
  return registry;
}
